// ** React Imports
import { Fragment, type SyntheticEvent, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';

// ** Next Import
import { useRouter } from 'next/router';

// ** Utils
//import { hexToRGBA } from '@erikdakoda/ui/shared/colorUtils';

// ** Icon Imports
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';

// ** Custom Components Imports
import HorizontalNavItems from './HorizontalNavListItems';

// ** Theme Config Import
import themeConfig from '../../../../config';
import type { Settings } from '../../../../context/settingsContext';

// ** Types
import type { NavGroup } from '../../../../types';
import { hasActiveChild } from '../../../../layouts/utils';

// ** MUI Imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Fade from '@mui/material/Fade';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
//import Paper from '@mui/material/Paper';

// ** Third Party Imports
import clsx from 'clsx';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';

interface Props {
  item: NavGroup;
  settings: Settings;
  hasParent?: boolean;
  /** When provided (e.g. from parent group), close that menu on child selection. */
  onCloseMenu?: () => void;
}

const HorizontalNavGroup = (props: Props) => {
  'use memo';

  // ** Props
  const { item, hasParent, settings, onCloseMenu } = props;

  // ** Hooks & Vars
  const router = useRouter();
  const currentURL = router.asPath;
  const { skin, direction, menuTextHidden } = settings;
  const { navSubItemIcon, menuTextTruncate, horizontalMenuAnimation } = themeConfig.config;

  const popperOffsetHorizontal = direction === 'rtl' ? 22 : -22;
  const popperPlacement = direction === 'rtl' ? 'bottom-end' : 'bottom-start';
  const popperPlacementSubMenu = direction === 'rtl' ? 'left-start' : 'right-start';

  // ** States
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [popperElement, setPopperElement] = useState(null);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [referenceElement, setReferenceElement] = useState(null);

  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement: hasParent ? popperPlacementSubMenu : popperPlacement,
    modifiers: [
      {
        name: 'offset',
        enabled: true,
        options: {
          offset: hasParent ? [-8, 15] : [popperOffsetHorizontal, 5],
        },
      },
      {
        enabled: true,
        name: 'flip',
      },
    ],
  });

  const handleGroupOpen = (event: SyntheticEvent) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
    if (update) update();
  };

  const handleGroupClose = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleMenuToggleOnClick = (event: SyntheticEvent) => {
    if (anchorEl) {
      handleGroupClose();
    } else {
      handleGroupOpen(event);
    }
  };

  useEffect(() => {
    handleGroupClose();
  }, [router.asPath]);

  const icon = item.icon ? item.icon : navSubItemIcon;
  const toggleIcon = direction === 'rtl' ? 'caret-left' : 'caret-right';

  const AnimationWrapper = horizontalMenuAnimation ? Fade : Fragment;

  const childMenuGroupStyles = () => {
    if (attributes && attributes.popper) {
      if (direction === 'ltr') {
        if (attributes.popper['data-popper-placement'] === 'right-start') {
          return 'left';
        }
        if (attributes.popper['data-popper-placement'] === 'left-start') {
          return 'right';
        }
      } else {
        if (attributes.popper['data-popper-placement'] === 'right-start') {
          return 'right';
        }
        if (attributes.popper['data-popper-placement'] === 'left-start') {
          return 'left';
        }
      }
    }
  };

  const menuItem = (
    <ListItemButton
      aria-haspopup="true"
      className={clsx('top-nav-link', 'menu-group', {
        'Mui-selected': hasActiveChild(item, currentURL),
      })}
      selected={hasActiveChild(item, currentURL)}
      onClick={handleMenuToggleOnClick}
      sx={(theme) => ({
        ...(menuOpen ? { backgroundColor: 'action.hover' } : {}),
        ...(!hasParent
          ? {
              px: 4.5,
              borderRadius: 3.5,
              '&.active, &.active:hover': theme.mixins.activeMenuItem,
            }
          : { px: 4 }),
      })}
    >
      <Box
        sx={{
          gap: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        ref={setReferenceElement}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            ...(menuTextTruncate && { overflow: 'hidden' }),
          }}
        >
          <ListItemIcon sx={{ mr: hasParent ? 4 : menuTextHidden ? 0 : 2, minWidth: 'auto' }}>
            <FaIconUvilo icon={icon} size="lg" />
          </ListItemIcon>
          {(hasParent || !menuTextHidden) && (
            <ListItemText {...(menuTextTruncate && { slotProps: { primary: { noWrap: true } } })}>
              {item.title}
            </ListItemText>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.primary',
          }}
        >
          {item.badgeContent ? (
            <Chip
              label={item.badgeContent}
              color={item.badgeColor || 'primary'}
              sx={{
                mr: 1.5,
                height: 20,
                fontWeight: 500,
                '& .MuiChip-label': {
                  px: 1.5,
                  textTransform: 'capitalize',
                },
              }}
            />
          ) : null}
          <FaIconUvilo
            icon={hasParent ? toggleIcon : 'caret-down'}
            prefix="fas"
            sx={{ opacity: 0.5 }}
          />
        </Box>
      </Box>
    </ListItemButton>
  );

  return (
    <ClickAwayListener onClickAway={handleGroupClose}>
      <List
        className="horizontal-nav-group-list"
        component="div"
        sx={{ py: skin === 'bordered' ? 2.625 : 2.75 }}
      >
        {menuTextHidden && !hasParent ? <Tooltip title={item.title}>{menuItem}</Tooltip> : menuItem}
        <AnimationWrapper
          {...(horizontalMenuAnimation && {
            in: menuOpen,
            timeout: { exit: 300, enter: 400 },
          })}
        >
          <Box
            style={styles.popper}
            ref={setPopperElement}
            {...attributes.popper}
            sx={{
              zIndex: 'appBar',
              ...(!horizontalMenuAnimation && {
                display: menuOpen ? 'block' : 'none',
              }),
              pl: childMenuGroupStyles() === 'left' ? (skin === 'bordered' ? 2.5 : 2.25) : 0,
              pr: childMenuGroupStyles() === 'right' ? (skin === 'bordered' ? 2.5 : 2.25) : 0,
              ...(hasParent
                ? { position: 'fixed !important' }
                : { pt: skin === 'bordered' ? 5.5 : 5.75 }),
            }}
          >
            <NavigationMenu
              anchorEl={anchorEl}
              handleDropdownClose={handleGroupClose}
              direction={direction}
            >
              <HorizontalNavItems
                {...props}
                hasParent
                horizontalNavItems={item.children}
                onCloseMenu={onCloseMenu ?? handleGroupClose}
              />
            </NavigationMenu>
          </Box>
        </AnimationWrapper>
      </List>
    </ClickAwayListener>
  );
};

const NavigationMenu = ({
  anchorEl,
  handleDropdownClose,
  direction,
  children,
}: {
  anchorEl: Element | null;
  handleDropdownClose: () => void;
  direction: 'ltr' | 'rtl';
  children: React.ReactNode;
}) => (
  <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={() => handleDropdownClose()}
    sx={{
      '& .MuiMenu-paper': {
        mt: '14px',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderTop: 'none',
      },
    }}
    anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
  >
    {children}
  </Menu>
);

export default HorizontalNavGroup;
